/* eslint-disable max-lines */
/* eslint-disable max-len */

import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Days from "../../../components/lines-managements/trips/Days";
import EnhancedDayPicker, { SelectedDateType } from "../../../components/lines-managements/trips/EnhancedDayPicker";
import CreateTripWrapper from "../../../components/lines-managements/trips/schedules/CreateTripWrapper";
import Box from "../../../components/ui/Box";
import Button from "../../../components/ui/Button";
import SpinnerLoader from "../../../components/ui/SpinnerLoader";
import BulkAffectedReservationsModal from "../../../components/lines-managements/trips/BulkAffectedReservationsModal";
import { BulkUpdateLocationState, IBulkNotifyTripEntry, IBulkUpdatedTripAffected, ITripTimeUpdate, TripPreviewRow } from "../../../interfaces/endpoints/trips/IBulkEditTripsWorkingDays";
import { CustomTripWorkingDayType, IOperationalInterval } from "../../../interfaces/endpoints/trips/Trips.types";
import { bulkUpdateTripsPrices } from "../../../services/Pricing.service";
import { bulkEditTripsWorkingDays, bulkUpdateTripsTimes, notifyBulkTripsChangeEmails } from "../../../services/Trips.service";
import "../../../styles/shared-calendar.css";

type BulkUpdateMode = "workingDays" | "prices" | "times";

const MODE_OPTIONS: { value: BulkUpdateMode; label: string }[] = [
  { value: "workingDays", label: "Ditët e udhëtimit" },
  { value: "prices", label: "Çmimet" },
  { value: "times", label: "Oraret" }
];

// Render operational interval like in Trips list
const renderOperationalInterval = (row: TripPreviewRow): React.ReactNode => {
  if (row.hasCustomDates && row.customWorkingDays && row.customWorkingDays.length > 0) {
    const exceptionDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.ExceptionDate
    );
    const workingDates = row.customWorkingDays.filter(
      (d) => d.type === CustomTripWorkingDayType.SelectedWorkingDate
    );

    return (
      <div className="space-y-1">
        <span className="text-xs text-gray-600 italic block">
          Ka data specifike të punës
        </span>
        <div className="flex flex-wrap gap-1">
          {exceptionDates.length > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              Përjashtimet: {format(new Date(exceptionDates[0].date), "dd/MM/yyyy")}
              {exceptionDates.length > 1 && (
                <span className="ml-1 text-gray-500">
                  +{exceptionDates.length - 1}
                </span>
              )}
            </span>
          )}
          {workingDates.length > 0 && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                font-medium bg-[#fef7f5] text-[#f05a2d]"
            >
              Datat e punës: {format(new Date(workingDates[0].date), "dd/MM/yyyy")}
              {workingDates.length > 1 && (
                <span className="ml-1 text-gray-500">
                  +{workingDates.length - 1}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <Days
      days={[
        row.operationalInterval.monday,
        row.operationalInterval.tuesday,
        row.operationalInterval.wednesday,
        row.operationalInterval.thursday,
        row.operationalInterval.friday,
        row.operationalInterval.saturday,
        row.operationalInterval.sunday
      ]}
    />
  );
};

// Convert "HH:mm:ss" or "HH:mm" departure/arrival string to "HH:mm"
const toTimeInputValue = (timeStr: string): string => {
  if (!timeStr) return "";
  const parts = timeStr.split(":");
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
  }
  return timeStr;
};

// Parse "HH:mm" to total minutes
const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

// Convert total minutes to "HH:mm" (wraps at 24h)
const minutesToTime = (mins: number): string => {
  const wrapped = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

// Get original duration in minutes (arrival - departure, handles overnight)
const getOriginalDuration = (dep: string, arr: string): number => {
  const depMin = timeToMinutes(toTimeInputValue(dep));
  const arrMin = timeToMinutes(toTimeInputValue(arr));
  let duration = arrMin - depMin;
  if (duration <= 0) duration += 1440;
  return duration;
};

const BulkUpdateTrips = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as BulkUpdateLocationState | null;

  // Bulk update mode
  const [bulkUpdateMode, setBulkUpdateMode] = useState<BulkUpdateMode>("workingDays");

  // Track selected trip IDs (allows deselection)
  const [selectedTripIds, setSelectedTripIds] = useState<Set<number>>(
    () => new Set(state?.tripIds ?? [])
  );

  // Working days type (ExceptionDate vs. SelectedWorkingDate)
  const [workingDaysType, setWorkingDaysType] = useState<CustomTripWorkingDayType>(
    CustomTripWorkingDayType.ExceptionDate
  );

  // Operational interval (weekly days)
  const [operationalInterval, setOperationalInterval] = useState<IOperationalInterval>({
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true
  });

  // Custom dates selection
  const [selectedDatesCustom, setSelectedDatesCustom] = useState<SelectedDateType[]>([]);

  // Price update state
  const [ageGroupId, setAgeGroupId] = useState<number>(1);
  const [oneWayPrice, setOneWayPrice] = useState<string>("");
  const [roundTripPrice, setRoundTripPrice] = useState<string>("");

  // Original durations per trip (in minutes), computed once from initial data
  const [tripDurations] = useState<Map<number, number>>(() => {
    const map = new Map<number, number>();
    if (state?.tripPreviewRows) {
      state.tripPreviewRows.forEach((row) => {
        map.set(row.tripId, getOriginalDuration(row.departureTimeString, row.arrivalTimeString));
      });
    }
    return map;
  });

  // Times update state: map of tripId -> { departureTime, arrivalTime }
  const [tripTimes, setTripTimes] = useState<Map<number, { departureTime: string; arrivalTime: string }>>(() => {
    const map = new Map<number, { departureTime: string; arrivalTime: string }>();
    if (state?.tripPreviewRows) {
      state.tripPreviewRows.forEach((row) => {
        map.set(row.tripId, {
          departureTime: toTimeInputValue(row.departureTimeString),
          arrivalTime: toTimeInputValue(row.arrivalTimeString)
        });
      });
    }
    return map;
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bulk affected reservations modal state
  const [showBulkAffectedModal, setShowBulkAffectedModal] = useState(false);
  const [bulkAffected, setBulkAffected] = useState<IBulkUpdatedTripAffected[]>([]);

  const tripLabelById = useMemo<Record<number, string>>(() => {
    if (!state?.tripPreviewRows) return {};
    return state.tripPreviewRows.reduce<Record<number, string>>((acc, row) => {
      acc[row.tripId] = row.routeName;
      return acc;
    }, {});
  }, [state?.tripPreviewRows]);

  // Handle trip selection toggle
  const handleTripToggle = useCallback((tripId: number) => {
    setSelectedTripIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(tripId)) {
        newSet.delete(tripId);
      } else {
        newSet.add(tripId);
      }
      return newSet;
    });
  }, []);

  // Handle select/deselect all trips
  const handleToggleAll = useCallback(() => {
    if (!state?.tripIds) return;
    setSelectedTripIds((prev) => {
      if (prev.size === state.tripIds.length) {
        return new Set();
      }
      return new Set(state.tripIds);
    });
  }, [state?.tripIds]);

  // Handle operational interval checkbox change
  const handleDayChange = useCallback((day: keyof IOperationalInterval) => {
    setOperationalInterval((prev) => ({
      ...prev,
      [day]: !prev[day]
    }));
  }, []);

  // Handle date selection change
  const handleDateSelectionChange = useCallback((dates: SelectedDateType[]) => {
    setSelectedDatesCustom(dates);
  }, []);

  // Handle working days type change
  const handleWorkingDaysTypeChange = useCallback((newType: CustomTripWorkingDayType) => {
    setWorkingDaysType(newType);
    setSelectedDatesCustom([]);
  }, []);

  // Handle trip time change — auto-calculate arrival from departure based on original duration
  const handleTripTimeChange = useCallback((tripId: number, field: "departureTime" | "arrivalTime", value: string) => {
    const duration = tripDurations.get(tripId) || 0;
    const isComplete = /^\d{2}:\d{2}$/.test(value);
    setTripTimes((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(tripId) || { departureTime: "", arrivalTime: "" };
      if (isComplete && field === "departureTime") {
        const depMinutes = timeToMinutes(value);
        newMap.set(tripId, {
          departureTime: value,
          arrivalTime: minutesToTime(depMinutes + duration)
        });
      } else {
        newMap.set(tripId, { ...current, [field]: value });
      }
      return newMap;
    });
  }, [tripDurations]);

  // Submit bulk update for working days. Returns true when the affected-reservations modal will be shown
  // and the caller should defer navigation.
  const handleSubmitWorkingDays = useCallback(async (): Promise<boolean> => {
    const customWorkingDays = selectedDatesCustom.map((d) => ({
      type: workingDaysType,
      date: new Date(Date.UTC(
        d.date.getFullYear(),
        d.date.getMonth(),
        d.date.getDate(),
        12, 0, 0, 0
      ))
    }));

    const tripIdsArray = Array.from(selectedTripIds);

    const response = await bulkEditTripsWorkingDays({
      tripIds: tripIdsArray,
      operationalInterval,
      customWorkingDays
    });

    const result = response.data;
    if (result.failedTripIds && result.failedTripIds.length > 0) {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan. ${result.failedTripIds.length} dështuan.`,
        { position: "top-right" }
      );
    } else {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );
    }

    const affectedWithPassengers = (result.affectedByTrip ?? [])
      .filter((x) => x.affected?.affectedPassengers?.length > 0);

    if (affectedWithPassengers.length > 0) {
      setBulkAffected(affectedWithPassengers);
      setShowBulkAffectedModal(true);
      return true;
    }

    return false;
  }, [selectedTripIds, operationalInterval, selectedDatesCustom, workingDaysType]);

  // Submit bulk update for prices
  const handleSubmitPrices = useCallback(async (): Promise<boolean> => {
    const parsedOneWay = parseFloat(oneWayPrice);
    const parsedRoundTrip = parseFloat(roundTripPrice);

    if (isNaN(parsedOneWay) || parsedOneWay < 0) {
      toast.error("Ju lutem vendosni një çmim valid për një drejtim.");
      return false;
    }
    if (isNaN(parsedRoundTrip) || parsedRoundTrip < 0) {
      toast.error("Ju lutem vendosni një çmim valid për vajtje-ardhje.");
      return false;
    }

    const tripIdsArray = Array.from(selectedTripIds);

    const response = await bulkUpdateTripsPrices({
      tripIds: tripIdsArray,
      ageGroupId,
      oneWayPrice: parsedOneWay,
      roundTripPrice: parsedRoundTrip
    });

    const result = response.data;
    if (result.failedTripIds && result.failedTripIds.length > 0) {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan. ${result.failedTripIds.length} dështuan.`,
        { position: "top-right" }
      );
    } else {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );
    }
    return false;
  }, [selectedTripIds, ageGroupId, oneWayPrice, roundTripPrice]);

  // Submit bulk update for times. Returns true when the affected-reservations modal will be shown
  // and the caller should defer navigation.
  const handleSubmitTimes = useCallback(async (): Promise<boolean> => {
    const tripIdsArray = Array.from(selectedTripIds);
    const trips: ITripTimeUpdate[] = [];
    for (let i = 0; i < tripIdsArray.length; i++) {
      const tripId = tripIdsArray[i];
      const times = tripTimes.get(tripId);
      if (!times || !times.departureTime || !times.arrivalTime) {
        toast.error("Ju lutem plotësoni orarin e nisjes dhe mbërritjes për të gjitha oraret e zgjedhura.");
        return false;
      }
      trips.push({
        tripId,
        departureTime: `${times.departureTime}:00`,
        arrivalTime: `${times.arrivalTime}:00`
      });
    }

    const response = await bulkUpdateTripsTimes({ trips });

    const result = response.data;
    if (result.failedTripIds && result.failedTripIds.length > 0) {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan. ${result.failedTripIds.length} dështuan.`,
        { position: "top-right" }
      );
    } else {
      toast.success(
        `${result.updatedTripIds.length} orare u përditësuan me sukses.`,
        { position: "top-right" }
      );
    }

    const affectedWithPassengers = (result.affectedByTrip ?? [])
      .filter((x) => x.affected?.affectedPassengers?.length > 0);

    if (affectedWithPassengers.length > 0) {
      setBulkAffected(affectedWithPassengers);
      setShowBulkAffectedModal(true);
      return true;
    }

    return false;
  }, [selectedTripIds, tripTimes]);

  // Submit handler
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedTripIds.size === 0) {
      toast.error("Nuk ka orare të zgjedhura për përditësim.");
      return;
    }

    setIsSubmitting(true);

    try {
      let deferNavigation = false;
      if (bulkUpdateMode === "workingDays") {
        deferNavigation = await handleSubmitWorkingDays();
      } else if (bulkUpdateMode === "prices") {
        deferNavigation = await handleSubmitPrices();
      } else {
        deferNavigation = await handleSubmitTimes();
      }

      if (!deferNavigation) {
        setTimeout(() => {
          navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
        }, 500);
      }
    } catch (error) {
      console.error("Bulk update failed:", error);
      toast.error("Ndodhi një gabim gjatë përditësimit të orareve.", { position: "top-right" });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedTripIds, bulkUpdateMode, handleSubmitWorkingDays, handleSubmitPrices, handleSubmitTimes, navigate]);

  const handleBulkAffectedModalClose = useCallback(() => {
    setShowBulkAffectedModal(false);
    setBulkAffected([]);
    setTimeout(() => {
      navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
    }, 300);
  }, [navigate]);

  const handleBulkAffectedConfirm = useCallback(async (selection: IBulkNotifyTripEntry[]) => {
    try {
      const response = await notifyBulkTripsChangeEmails({ trips: selection });
      if (response.data) {
        toast.success("Njoftimet me email u dërguan me sukses.", { position: "top-right" });
      } else {
        toast.error("Ndodhi një gabim gjatë dërgimit të njoftimeve me email.", { position: "top-right" });
      }
    } catch (error) {
      toast.error("Ndodhi një gabim gjatë dërgimit të njoftimeve me email.", { position: "top-right" });
    }

    setShowBulkAffectedModal(false);
    setBulkAffected([]);
    setTimeout(() => {
      navigate("/trips", { state: ["Menaxhimi i linjave", "Oraret"] });
    }, 500);
  }, [navigate]);

  // If no state is passed, show empty state
  if (!state?.tripIds || state.tripIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <h2 className="text-xl font-semibold text-gray-700 mb-4">
          Nuk ka orare të zgjedhura
        </h2>
        <p className="text-gray-500 mb-6">
          Ju lutem zgjidhni oraret që dëshironi të përditësoni nga faqja e orareve.
        </p>
        <Link
          to="/trips"
          className="flex items-center text-primary hover:underline"
          state={["Menaxhimi i linjave", "Oraret"]}
        >
          <ChevronLeftIcon className="w-4 h-4 mr-1" />
          Kthehu tek oraret
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/trips"
        state={["Menaxhimi i linjave", "Oraret"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek oraret</span>
      </Link>

      <Box className="p-0">
        <form onSubmit={handleSubmit} className="flex flex-col">
          <h1 className="text-xl mb-2 py-3 px-5 border-b">
            Përditëso {selectedTripIds.size} orare
          </h1>

          <div className="p-5 overflow-auto">
            {/* Selected trips preview */}
            <CreateTripWrapper title="Oraret e zgjedhura" className="border-none mb-6">
              <div className="max-h-72 overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="py-2 px-3 w-10">
                        <input
                          type="checkbox"
                          className="cursor-pointer"
                          checked={selectedTripIds.size === state.tripIds.length}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = selectedTripIds.size > 0 &&
                              selectedTripIds.size < state.tripIds.length;
                            }
                          }}
                          onChange={handleToggleAll}
                          title={selectedTripIds.size === state.tripIds.length ?
                             "Çzgjidh të gjitha" : "Zgjidh të gjitha"}
                        />
                      </th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Linja</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Orari</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Ditët e udhëtimit</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Një drejtim</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Vajtje-ardhje</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {state.tripPreviewRows.map((row) => (
                      <tr
                        key={row.tripId}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          !selectedTripIds.has(row.tripId) ? "opacity-50" : ""
                        }`}
                        onClick={() => handleTripToggle(row.tripId)}
                      >
                        <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="cursor-pointer"
                            checked={selectedTripIds.has(row.tripId)}
                            onChange={() => handleTripToggle(row.tripId)}
                          />
                        </td>
                        <td className="py-2 px-3">{row.routeName}</td>
                        <td className="py-2 px-3 whitespace-nowrap">
                          {row.departureTimeString} - {row.arrivalTimeString}
                        </td>
                        <td className="py-2 px-3">
                          {renderOperationalInterval(row)}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {row.prices ? `${row.prices[0].toFixed(2)}€` : "-"}
                        </td>
                        <td className="py-2 px-3 text-right whitespace-nowrap">
                          {row.prices ? `${row.prices[1].toFixed(2)}€` : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CreateTripWrapper>

            {/* Mode selector */}
            <div className="flex rounded-lg border border-gray-300 overflow-hidden w-fit mb-5">
              {MODE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setBulkUpdateMode(option.value)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    bulkUpdateMode === option.value
                      ? "bg-primary text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  } ${option.value !== "workingDays" ? "border-l border-gray-300" : ""}`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Working days selection */}
            {bulkUpdateMode === "workingDays" && (
              <CreateTripWrapper title="Ditët e udhëtimit" className="border-none mt-5">
                <div className="space-y-4">
                  <div className="flex gap-6 md:px-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="workingDaysType"
                        checked={workingDaysType === CustomTripWorkingDayType.ExceptionDate}
                        onChange={() =>
                          handleWorkingDaysTypeChange(CustomTripWorkingDayType.ExceptionDate)
                        }
                        className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">Përsëritje javore me përjashtime</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="workingDaysType"
                        checked={workingDaysType === CustomTripWorkingDayType.SelectedWorkingDate}
                        onChange={() =>
                          handleWorkingDaysTypeChange(CustomTripWorkingDayType.SelectedWorkingDate)
                        }
                        className="w-4 h-4 accent-primary border-gray-300 focus:ring-primary"
                      />
                      <span className="ml-2 text-gray-700">Datat specifike të punës</span>
                    </label>
                  </div>

                  <div>
                    {workingDaysType === CustomTripWorkingDayType.ExceptionDate && (
                      <div className="flex-wrap flex gap-7 md:gap-0">
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="monday"
                            className="cursor-pointer"
                            checked={operationalInterval.monday}
                            onChange={() => handleDayChange("monday")}
                          />
                          <label
                            htmlFor="monday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Hënë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="tuesday"
                            className="cursor-pointer"
                            checked={operationalInterval.tuesday}
                            onChange={() => handleDayChange("tuesday")}
                          />
                          <label
                            htmlFor="tuesday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Martë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="wednesday"
                            className="cursor-pointer"
                            checked={operationalInterval.wednesday}
                            onChange={() => handleDayChange("wednesday")}
                          />
                          <label
                            htmlFor="wednesday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Mërkurë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="thursday"
                            className="cursor-pointer"
                            checked={operationalInterval.thursday}
                            onChange={() => handleDayChange("thursday")}
                          />
                          <label
                            htmlFor="thursday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Enjte
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="friday"
                            className="cursor-pointer"
                            checked={operationalInterval.friday}
                            onChange={() => handleDayChange("friday")}
                          />
                          <label
                            htmlFor="friday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Premte
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="saturday"
                            className="cursor-pointer"
                            checked={operationalInterval.saturday}
                            onChange={() => handleDayChange("saturday")}
                          />
                          <label
                            htmlFor="saturday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Shtunë
                          </label>
                        </div>
                        <div className="md:px-4 flex items-center">
                          <input
                            type="checkbox"
                            id="sunday"
                            className="cursor-pointer"
                            checked={operationalInterval.sunday}
                            onChange={() => handleDayChange("sunday")}
                          />
                          <label
                            htmlFor="sunday"
                            className="ml-3 text-sm text-neutral cursor-pointer"
                          >
                            E Diel
                          </label>
                        </div>
                      </div>
                    )}
                    <div className="w-full flex justify-center mt-4">
                      <div className="w-[350px]">
                        <EnhancedDayPicker
                          selectedDates={selectedDatesCustom}
                          onChange={handleDateSelectionChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CreateTripWrapper>
            )}

            {/* Prices update */}
            {bulkUpdateMode === "prices" && (
              <CreateTripWrapper title="Përditëso çmimet" className="border-none mt-5">
                <div className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grupi i moshës
                    </label>
                    <select
                      value={ageGroupId}
                      onChange={(e) => setAgeGroupId(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                        focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    >
                      <option value={1}>I/e rritur</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="oneWayPrice" className="block text-sm font-medium text-gray-700 mb-1">
                      Çmimi një drejtim (€)
                    </label>
                    <input
                      id="oneWayPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={oneWayPrice}
                      onChange={(e) => setOneWayPrice(e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                        focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <div>
                    <label htmlFor="roundTripPrice" className="block text-sm font-medium text-gray-700 mb-1">
                      Çmimi vajtje-ardhje (€)
                    </label>
                    <input
                      id="roundTripPrice"
                      type="number"
                      min="0"
                      step="0.01"
                      value={roundTripPrice}
                      onChange={(e) => setRoundTripPrice(e.target.value)}
                      placeholder="0.00"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm
                        focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  <p className="text-xs text-gray-500">
                    Çmimet e njëjta do të aplikohen për të gjitha oraret e zgjedhura dhe të gjitha kombinacionet e stacioneve.
                  </p>
                </div>
              </CreateTripWrapper>
            )}

            {/* Times update */}
            {bulkUpdateMode === "times" && (
              <CreateTripWrapper title="Përditëso oraret e nisjes dhe mbërritjes" className="border-none mt-5">
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Linja</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Orari aktual</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Nisja e re</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Mbërritja e re</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {state.tripPreviewRows
                        .filter((row) => selectedTripIds.has(row.tripId))
                        .map((row) => {
                          const times = tripTimes.get(row.tripId) || { departureTime: "", arrivalTime: "" };
                          return (
                            <tr key={row.tripId} className="hover:bg-gray-50">
                              <td className="py-2 px-3">{row.routeName}</td>
                              <td className="py-2 px-3 whitespace-nowrap text-gray-500">
                                {row.departureTimeString} - {row.arrivalTimeString}
                              </td>
                              <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="time"
                                  lang="sq"
                                  value={times.departureTime}
                                  onChange={(e) => handleTripTimeChange(row.tripId, "departureTime", e.target.value)}
                                  className="border border-gray-300 rounded-md px-2 py-1 text-sm
                                    focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                              </td>
                              <td className="py-2 px-3" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="time"
                                  lang="sq"
                                  value={times.arrivalTime}
                                  onChange={(e) => handleTripTimeChange(row.tripId, "arrivalTime", e.target.value)}
                                  className="border border-gray-300 rounded-md px-2 py-1 text-sm
                                    focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Çdo orar ka kohën e vet të nisjes dhe mbërritjes. Kohët përditësohen vetëm për oraret e zgjedhura.
                </p>
              </CreateTripWrapper>
            )}
          </div>

          {/* Footer with submit button */}
          <div className="border-t p-4 justify-end flex sticky bottom-0 rounded-b-lg bg-white z-10">
            <div>
              <Button
                btnType="custom"
                className="border border-primary text-primary mr-4"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                Anulo
              </Button>
              <Button type="submit" disabled={isSubmitting || selectedTripIds.size === 0}>
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <SpinnerLoader />
                    Duke përditësuar...
                  </span>
                ) : (
                  "Ruaj ndryshimet"
                )}
              </Button>
            </div>
          </div>
        </form>
      </Box>

      <BulkAffectedReservationsModal
        isOpen={showBulkAffectedModal}
        onClose={handleBulkAffectedModalClose}
        onConfirm={handleBulkAffectedConfirm}
        affectedByTrip={bulkAffected}
        tripLabelById={tripLabelById}
      />
    </div>
  );
};

export default BulkUpdateTrips;
